import { Box, Group, type GroupProps, type MantineSize, Stack, Text } from "@mantine/core"
import { useAnonymous } from "@/hooks"
import type { TUser, TUserBase } from "@/types"
import { buildUserURL } from "@/utils"
import { AppLink } from "../utils"
import { UserAvatar } from "./UserAvatar"

interface IProps extends GroupProps {
  url?: string
  user?: TUserBase | TUser
  compact?: boolean
  size?: MantineSize
  position?: boolean
  isActive?: boolean
  shortName?: boolean
}

export function UserLine({
  user,
  size,
  url,
  compact,
  position,
  isActive = true,
  shortName,
  children,
  ...props
}: IProps) {
  const anonymous = useAnonymous()
  user = user || anonymous
  size = size || "lg"

  let name = user.full_name ?? user.id
  if (shortName) {
    const parts = name.split(" ")
    name = parts[0] || ""
  }

  return compact ? (
    <Group gap={4} wrap="nowrap" {...props} opacity={isActive ? 1 : 0.5}>
      <UserAvatar user={user} size="sm" mr={4} withLink />
      <Box>
        <AppLink href={url || buildUserURL(user.id)}>
          <Text fw={500} fz="inherit" style={{ textWrap: "nowrap" }}>
            {name}
          </Text>
        </AppLink>
        {position && user.position && (
          <>
            <Text>·</Text>
            <Text>{user.position}</Text>
          </>
        )}
        {children}
      </Box>
    </Group>
  ) : (
    <Group gap="sm" wrap="nowrap" {...props} opacity={isActive ? 1 : 0.5}>
      <UserAvatar user={user} size={size} withLink />
      <Stack gap={0}>
        <AppLink href={url}>
          <Text fz={size} fw={500} lineClamp={2}>
            {name}
          </Text>
        </AppLink>
        <Text fz="sm" c="dimmed" lineClamp={2}>
          {user.position}
        </Text>
        {children}
      </Stack>
    </Group>
  )
}
